/*
 * EVR Advertorial Insurance Calculator
 * -------------------------------------
 * Hydrates the static "Krankenkassen-Zuschuss" price card that lives in the
 * body HTML of the advertorial pages (page.bare template). It reuses the exact
 * same ZPP backend as the theme block `blocks/evr-insurance-check-v2.liquid`
 * (window.EVR.zpp config + /apps/eversion-backend/api/zpp/{insurers,refund}),
 * so the numbers stay consistent with the on-store calculator.
 *
 * It does NOT require any edit to the stored page content: it targets the
 * existing markup classes and only runs when a `.evr-offer__kasse` placeholder
 * is present. Expected markup (already on the page):
 *
 *   <div class="evr-offer__pricecard">
 *     <div class="evr-offer__kasse">BARMER <span>Beispiel ▾</span></div>
 *     <div class="evr-offer__row evr-offer__row--uvp">…<span class="v">399 €</span></div>
 *     <div class="evr-offer__row">Programmpreis…<span class="v">249 €</span></div>
 *     <div class="evr-offer__row evr-offer__row--erstattung">Erstattung …<span class="v">– 100 €</span></div>
 *     <div class="evr-offer__row evr-offer__row--final">Dein Preis …<span class="v">149 €</span></div>
 *     <div class="evr-offer__save">Du sparst insgesamt 250 € (63 % gegenüber UVP).</div>
 *   </div>
 */
(function () {
  'use strict';

  // Mirror the v2 block: surface the most common insurers at the top of the list.
  var TOP_INSURERS = [
    'Techniker Krankenkasse',
    'BARMER',
    'DAK-Gesundheit',
    'AOK PLUS',
    'IKK classic'
  ];

  // Refund is capped at the eligible amount when calling the API (only this much
  // of the program price is refundable). Display still uses the full price.
  var REFUND_ELIGIBLE_MAX = 150;

  // Initial insurer to show so the card is not empty on load.
  // Overridable via ?insurer=NAME in the URL.
  var DEFAULT_INSURER = 'Techniker Krankenkasse';

  // CTA under the selector ("…Zuschuss prüfen") points here, carrying the
  // chosen insurer so the product landing can preselect it.
  // e.g. /products/ganganalyse-praventionskurs-1&insurer=Techniker+Krankenkasse
  var CTA_BASE = '/products/ganganalyse-praventionskurs-1';
  // Leer = Standard-Template des Produkts (seit 10/2026 die gemeinsame Seite praev-alt-2).
  var CTA_VIEW = '';

  function cfg(key, fallback) {
    try {
      if (window.EVR && window.EVR.zpp && window.EVR.zpp[key]) return window.EVR.zpp[key];
    } catch (_) {}
    return fallback;
  }

  var API_BASE = cfg('API_BASE', '/apps/eversion-backend/api/zpp');
  var KURS_ID = cfg('KURS_ID', '');

  var normalizeName = function (s) { return String(s || '').trim().toLowerCase(); };

  function formatEUR(n) {
    if (!isFinite(n)) return '—';
    try {
      return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(n) + ' €';
    } catch (_) {
      return Math.round(n) + ' €';
    }
  }

  // Parse "399 €", "1.249 €", "149,00 €" → number. German formatting:
  // dot = thousands separator, comma = decimal.
  function parsePrice(str) {
    if (!str) return NaN;
    var s = String(str).replace(/[^\d.,]/g, '');
    s = s.replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
    return parseFloat(s);
  }

  function getInsurerFromUrl() {
    try {
      return (new URL(window.location.href).searchParams.get('insurer') || '').trim();
    } catch (_) {
      return '';
    }
  }

  function fetchJson(url, params) {
    var qs = new URLSearchParams(params).toString();
    return fetch(url + '?' + qs, { method: 'GET', credentials: 'same-origin' }).then(function (res) {
      return res.text().then(function (text) {
        var data = {};
        try { data = text ? JSON.parse(text) : {}; } catch (_) { data = {}; }
        if (!res.ok) throw new Error((data && (data.detail || data.error)) || ('HTTP ' + res.status));
        return data;
      });
    });
  }

  function injectStylesOnce() {
    if (document.getElementById('evr-kk-select-styles')) return;
    var st = document.createElement('style');
    st.id = 'evr-kk-select-styles';
    st.textContent =
      '.evr-offer__kasse--live{padding:0;}' +
      '.evr-kk-select{' +
        'width:100%;border:0;outline:0;background:transparent;' +
        'font:inherit;font-weight:600;color:#1a1a1a;cursor:pointer;' +
        'padding:11px 38px 11px 13px;border-radius:6px;' +
        '-webkit-appearance:none;-moz-appearance:none;appearance:none;' +
        "background-image:url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><path d='m6 9 6 6 6-6'/></svg>\");" +
        'background-repeat:no-repeat;background-position:right 13px center;' +
      '}' +
      '.evr-kk-select:focus-visible{box-shadow:0 0 0 3px rgba(63,174,90,.25);}';
    document.head.appendChild(st);
  }

  function hydrate(card) {
    if (card.getAttribute('data-evr-kk-inited') === '1') return;

    var kasseBox = card.querySelector('.evr-offer__kasse');
    if (!kasseBox) return;
    card.setAttribute('data-evr-kk-inited', '1');

    // --- Read prices from the existing rows -------------------------------
    var rows = Array.prototype.slice.call(card.querySelectorAll('.evr-offer__row'));
    var uvpRow = card.querySelector('.evr-offer__row--uvp');
    var erstattungRow = card.querySelector('.evr-offer__row--erstattung');
    var finalRow = card.querySelector('.evr-offer__row--final');
    var saveEl = card.querySelector('.evr-offer__save');

    // Programmpreis = the plain .evr-offer__row (no modifier class).
    var preisRow = rows.filter(function (r) { return r.classList.length === 1; })[0] ||
      rows.filter(function (r) { return /Programmpreis/i.test(r.textContent || ''); })[0];

    var uvp = uvpRow ? parsePrice((uvpRow.querySelector('.v') || {}).textContent) : NaN;
    var preis = preisRow ? parsePrice((preisRow.querySelector('.v') || {}).textContent) : NaN;
    if (!isFinite(preis) || preis <= 0) preis = 249;
    var uvpSafe = (isFinite(uvp) && uvp > 0) ? uvp : 0;
    var preisForRefund = Math.min(preis, REFUND_ELIGIBLE_MAX);

    // --- Build the select in place of the static "BARMER ▾" --------------
    injectStylesOnce();
    kasseBox.classList.add('evr-offer__kasse--live');
    kasseBox.innerHTML = '';
    var select = document.createElement('select');
    select.className = 'evr-kk-select';
    select.setAttribute('aria-label', 'Krankenkasse auswählen');
    var opt0 = document.createElement('option');
    opt0.value = '';
    opt0.textContent = 'Krankenkasse wählen…';
    select.appendChild(opt0);
    kasseBox.appendChild(select);

    var erstattungLabel = erstattungRow ? erstattungRow.querySelector('span:not(.v)') : null;
    var erstattungVal = erstattungRow ? erstattungRow.querySelector('.v') : null;
    var finalVal = finalRow ? finalRow.querySelector('.v') : null;

    // CTA ("…Zuschuss prüfen") lives next to the price card inside .evr-offer.
    var offer = card.closest('.evr-offer') || card.parentNode || document;
    var cta = offer.querySelector('.evr-offer__cta');

    // The static "Beispielwerte für …" note is obsolete once the calculator
    // shows live, insurer-specific values — hide it.
    var note = offer.querySelector('.evr-offer__note');
    if (note) note.style.display = 'none';

    function applyInsurerToLinks(insurer) {
      if (!insurer) return;

      // Dedicated "…Zuschuss prüfen" CTA → the shared product page.
      if (cta) {
        try {
          var u = new URL(CTA_BASE, window.location.origin);
          if (CTA_VIEW) u.searchParams.set('view', CTA_VIEW);
          u.searchParams.set('insurer', insurer);
          cta.href = u.toString();
        } catch (_) {}
      }

      // Every other product link/CTA on the page keeps its own destination but
      // carries the chosen insurer, so the selection follows the customer into
      // the funnel no matter which CTA they click.
      var links = document.querySelectorAll('a[href*="/products/"]');
      Array.prototype.forEach.call(links, function (a) {
        if (a === cta) return;
        try {
          var lu = new URL(a.href, window.location.origin);
          lu.searchParams.set('insurer', insurer);
          a.href = lu.toString();
        } catch (_) {}
      });
    }

    function setBreakdown(insurer, amount) {
      var effective = isFinite(amount) ? Math.max(0, preis - amount) : preis;
      if (erstattungLabel) erstattungLabel.textContent = 'Erstattung ' + insurer;
      if (erstattungVal) erstattungVal.textContent = isFinite(amount) ? ('– ' + formatEUR(amount)) : '–';
      if (finalVal) finalVal.textContent = formatEUR(effective);
      if (saveEl) {
        if (uvpSafe > 0 && effective < uvpSafe) {
          var saved = uvpSafe - effective;
          var pct = Math.round((saved / uvpSafe) * 100);
          saveEl.textContent = 'Du sparst insgesamt ' + formatEUR(saved) + ' (' + pct + ' % gegenüber UVP).';
          saveEl.style.display = '';
        } else {
          saveEl.style.display = 'none';
        }
      }
    }

    // Display only: fetch the refund and update the breakdown rows. Does NOT
    // touch the CTA links or fire the selection event — so the initial default
    // (Techniker Krankenkasse) shows numbers without putting &insurer on links.
    // isSelection=true haengt select=1 an -> nur dann zaehlt das Backend die
    // Kasse als aktive QUERY-Auswahl. Der Default-Vorabload ruft ohne Flag auf
    // und wird NICHT gezaehlt (keine Statistik-Verfaelschung durch die Default-Kasse).
    function loadRefund(insurer, isSelection) {
      if (!insurer) return;
      if (erstattungVal) erstattungVal.textContent = '…';
      if (finalVal) finalVal.textContent = '…';
      var refundParams = { preis: String(preisForRefund), kursId: KURS_ID, insurer: insurer };
      if (isSelection) refundParams.select = '1';
      fetchJson(API_BASE + '/refund', refundParams)
        .then(function (data) {
          setBreakdown(insurer, Number(data && data.amount));
        })
        .catch(function () {
          setBreakdown(insurer, NaN);
        });
    }

    // Active selection (user change or ?insurer= link): propagate the insurer to
    // all CTAs and feed the PURCHASE statistic, then show the breakdown.
    function selectInsurer(insurer) {
      if (!insurer) return;
      applyInsurerToLinks(insurer);
      try {
        window.dispatchEvent(new CustomEvent('evr:zpp:selection', { detail: { insurer: insurer } }));
      } catch (_) {}
      loadRefund(insurer, true);
    }

    // --- Populate the insurer list ---------------------------------------
    fetchJson(API_BASE + '/insurers', { preis: String(preisForRefund), kursId: KURS_ID })
      .then(function (data) {
        var insurers = (data && Array.isArray(data.insurers)) ? data.insurers : [];
        var seen = {};
        var all = insurers
          .map(function (n) { return String(n || '').trim(); })
          .filter(function (n) {
            if (!n) return false;
            var k = normalizeName(n);
            if (seen[k]) return false;
            seen[k] = 1;
            return true;
          })
          .sort(function (a, b) { return a.localeCompare(b, 'de', { sensitivity: 'base' }); });

        var byKey = {};
        all.forEach(function (n) { byKey[normalizeName(n)] = n; });

        function addOption(name) {
          var o = document.createElement('option');
          o.value = name;
          o.textContent = name;
          select.appendChild(o);
        }

        TOP_INSURERS
          .map(function (t) { return byKey[normalizeName(t)] || null; })
          .filter(Boolean)
          .forEach(addOption);

        var sep = document.createElement('option');
        sep.value = '__sep__';
        sep.textContent = '──────────';
        sep.disabled = true;
        select.appendChild(sep);

        all.forEach(addOption);

        // ?insurer=NAME counts as a carried-over selection → propagate to CTAs.
        // The DEFAULT only shows the breakdown; it must NOT put &insurer on the
        // links until the user actively chooses a Krankenkasse.
        var fromUrl = getInsurerFromUrl();
        var urlInsurer = fromUrl ? (byKey[normalizeName(fromUrl)] || '') : '';
        if (urlInsurer) {
          select.value = urlInsurer;
          selectInsurer(urlInsurer);
        } else {
          var def = byKey[normalizeName(DEFAULT_INSURER)] || '';
          if (def) {
            select.value = def;
            loadRefund(def);
          }
        }
      })
      .catch(function () {
        // On failure, leave the static example values untouched.
      });

    select.addEventListener('change', function () {
      var insurer = select.value;
      if (!insurer || insurer === '__sep__') return;
      try {
        var here = new URL(window.location.href);
        here.searchParams.set('insurer', insurer);
        window.history.replaceState({}, '', here.toString());
      } catch (_) {}
      selectInsurer(insurer);
    });
  }

  function init() {
    var cards = document.querySelectorAll('.evr-offer__pricecard');
    Array.prototype.forEach.call(cards, hydrate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
